import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getAuthHeaders } from '../../utils/authHeaders';

export interface UserSettings {
  theme: 'light' | 'dark';
  language: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  display: {
    compactMode: boolean;
    fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  };
  privacy: {
    // Privacy settings can be added here in the future
  };
  security: {
    idleTimeout: number; // in minutes
    enableIdleTimeout: boolean;
  };
}

const defaultSettings: UserSettings = {
  theme: 'light',
  language: 'en',
  notifications: {
    email: true,
    push: true,
    sms: false,
  },
  display: {
    compactMode: false,
    fontSize: 'medium',
  },
  privacy: {
    // Privacy settings can be added here in the future
  },
  security: {
    idleTimeout: 2, // 2 minutes
    enableIdleTimeout: true,
  },
};

interface SettingsState {
  settings: UserSettings | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  settings: defaultSettings,
  isLoading: false,
  error: null,
};

// Async thunks for API calls
export const loadSettings = createAsyncThunk(
  'settings/loadSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/user-management/settings', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load settings: ${response.status}`);
      }

      const data = await response.json();
      return data.settings || defaultSettings;
    } catch (error) {
      console.warn('Using default settings (API error):', error);
      return defaultSettings;
    }
  }
);

export const saveSettings = createAsyncThunk(
  'settings/saveSettings',
  async (newSettings: Partial<UserSettings>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { settings: SettingsState };
      const currentSettings = state.settings.settings;
      const updatedSettings = { ...currentSettings, ...newSettings };

      const response = await fetch('/api/user-management/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ settings: updatedSettings }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save settings: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Settings saved to API:', data.settings);
      return data.settings || updatedSettings;
    } catch (error) {
      console.error('❌ Failed to save settings:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save settings');
    }
  }
);


const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Initialize settings (no longer uses localStorage)
    initializeSettings: (state) => {
      // Settings are now loaded via API call
      state.settings = defaultSettings;
    },
    // Update settings locally (for immediate UI updates)
    updateSettingsLocally: (state, action: PayloadAction<Partial<UserSettings>>) => {
      console.log('🔄 updateSettingsLocally called with:', action.payload);
      console.log('🔄 Current state.settings:', state.settings);
      if (state.settings) {
        state.settings = { ...state.settings, ...action.payload };
        console.log('🔄 Updated state.settings:', state.settings);
      }
    },
    // Clear settings
    clearSettings: (state) => {
      state.settings = defaultSettings;
      state.error = null;
      state.isLoading = false;
    },
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
        console.log('Settings loaded from database:', action.payload);
      })
      .addCase(loadSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.settings = defaultSettings; // Fallback to defaults
      })
      .addCase(saveSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(saveSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  initializeSettings, 
  updateSettingsLocally, 
  clearSettings, 
  setLoading, 
  setError 
} = settingsSlice.actions;

export default settingsSlice.reducer;
